import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getAppOnlineStatus, updateMediaStatus, useConversationChannelData } from '../../../slice';
import { showNetworkErrorToast } from '../toast';
import { enqueueFile, handleMediaAction } from './mediaQueueManager';

export const mediaStatusConstants = {
    UPLOADING: 'UPLOADING',
    DOWNLOADING: 'DOWNLOADING',
    LOADED: 'LOADED',
    NOT_DOWNLOADED: 'NOT_DOWNLOADED',
    NOT_UPLOADED: 'NOT_UPLOADED',
    UNKNOWN: 'UNKNOWN',
};

export const useMediaProgress = ({
    channelId,
    uploadStatus = 0,
    downloadStatus = 0,
    messageId,
    autoDownload = false,
    fileSave = false,
    local_path,
}) => {
    const dispatch = useDispatch();
    const [mediaStatus, setMediaStatus] = React.useState('');

    const channelData = useConversationChannelData(channelId);
    const { messages = [] } = channelData || {};

    const messageObject = React.useMemo(() => {
        return messages.find(msg => msg.messageId === messageId) || null;
    }, [messages, messageId]);

    const handleDownload = useCallback(async () => {
        if (!messageObject) return;
        dispatch(updateMediaStatus({ channelId, messageId, is_downloaded: 1, is_uploading: 2 }));
        const { statusCode, local_path } = await handleMediaAction(messageObject, 'download', fileSave);
        if (statusCode === 200) {
            dispatch(updateMediaStatus({ channelId, messageId, is_downloaded: 2, is_uploading: 2, local_path }));
        }
    }, [messageObject, dispatch, channelId, messageId, fileSave]);

    React.useEffect(() => {
        switch (true) {
            case downloadStatus === 1:
                setMediaStatus(mediaStatusConstants.DOWNLOADING);
                break;

            case uploadStatus === 2 && downloadStatus === 0 && !local_path:
                setMediaStatus(mediaStatusConstants.NOT_DOWNLOADED);
                if (autoDownload) {
                    handleDownload();
                }
                break;
            case uploadStatus === 2 && downloadStatus === 3:
                setMediaStatus(mediaStatusConstants.NOT_DOWNLOADED);
                break;

            case uploadStatus === 2 && downloadStatus === 2:
                setMediaStatus(mediaStatusConstants.LOADED);
                break;

            case uploadStatus === 3 && downloadStatus === 2:
                setMediaStatus(mediaStatusConstants.NOT_UPLOADED);
                break;

            case [1, 0].includes(uploadStatus):
                setMediaStatus(mediaStatusConstants.UPLOADING);
                break;

            default:
                setMediaStatus(mediaStatusConstants.UNKNOWN);
        }
    }, [uploadStatus, downloadStatus, local_path, autoDownload, handleDownload]);

    const cancelProgress = useCallback(async () => {
        if (!messageObject) return;
        if ([1, 0].includes(uploadStatus)) {
            handleMediaAction(messageObject, 'cancelUpload');
        } else if (downloadStatus === 1) {
            await handleMediaAction(messageObject, 'cancelDownload');
            dispatch(
                updateMediaStatus({
                    channelId,
                    messageId,
                    is_downloaded: 3,
                }),
            );
        }
    }, [messageObject, dispatch, channelId, messageId, uploadStatus, downloadStatus]);

    const handleUploadResume = useCallback(() => {
        const isOnline = getAppOnlineStatus();
        if (!isOnline) return showNetworkErrorToast();
        if (!messageObject) return;

        const transformed = {
            fileDetails: {
                mentionedUsersIds: messageObject.content.media.mentionedUsersIds,
                caption: messageObject.content.media.caption,
                fileName: messageObject.content.media.fileName,
                duration: messageObject.content.media.duration,
                file_size: messageObject.content.media.file_size,
                file_url: messageObject.content.media.file_url,
                webWidth: messageObject.content.media.webWidth,
                webHeight: messageObject.content.media.webHeight,
                androidWidth: messageObject.content.media.androidWidth,
                androidHeight: messageObject.content.media.androidHeight,
                originalWidth: messageObject.content.media.originalWidth,
                originalHeight: messageObject.content.media.originalHeight,
                audioType: messageObject.content.media.audioType,
                thumb_image: messageObject.content.media.thumb_image,
            },
            messageId: messageObject.messageId,
            channelId: messageObject.channelId,
            file: messageObject.content.media.file,
        };
        enqueueFile(messageObject, messageObject.channelId, transformed, messageObject.messageType, true);
    }, [messageObject]);

    return {
        mediaStatus,
        downloadMedia: handleDownload,
        cancelProgress,
        retryUploadMedia: handleUploadResume,
    };
};

export default useMediaProgress;
